/**
 * a sampling decision carried by an error or a Response, read by ErrorReporter in beforeSend
 *
 * see docs/front-end/errors/error-reporting.md#error-sampling
 */
export type ErrorSampling = Readonly<{ sampleRate: number; sampled: boolean }>;

const responseSamplings = new WeakMap<Response, ErrorSampling>();
const errorSamplings = new WeakMap<Error, ErrorSampling>();

function isResponse(value: unknown): value is Response {
	return typeof Response !== 'undefined' && value instanceof Response;
}

/**
 * every report of this response, or of an error made from it, will use the same sampling decision
 *
 * errors made from the response must copy it with setErrorSamplingOnError
 */
export function setErrorSamplingOnResponse(response: Response, sampling: ErrorSampling): void {
	responseSamplings.set(response, sampling);
}

export function getErrorSamplingOnResponse(response: Response): ErrorSampling | undefined {
	return responseSamplings.get(response);
}

/**
 * every report of this error, or of an error caused by it, will use the same sampling decision
 */
export function setErrorSamplingOnError(error: Error, sampling: ErrorSampling): void {
	errorSamplings.set(error, sampling);
}

/**
 * returns the nearest sampling decision in the error and its causes chain
 *
 * the last cause, or the reported value itself, can be a Response
 */
export function getErrorSamplingOnError(error: unknown): ErrorSampling | undefined {
	const visited = new Set<unknown>();
	let current = error;
	while (current instanceof Error && !visited.has(current)) {
		visited.add(current);
		const sampling = errorSamplings.get(current);
		if (sampling) return sampling;
		current = current.cause;
	}
	return isResponse(current) ? getErrorSamplingOnResponse(current) : undefined;
}
